import React from "react";
import GlobalStyles, { CommonClasses } from "../global/GlobalStyle";
import {
  MdPrivacyTip,
  MdSecurity,
  MdDataUsage,
  MdCookie,
} from "react-icons/md";

const Privacy = () => {
  const PrivacyIcon = MdPrivacyTip as React.FC<React.SVGProps<SVGSVGElement>>;
  const SecurityIcon = MdSecurity as React.FC<React.SVGProps<SVGSVGElement>>;
  const DataIcon = MdDataUsage as React.FC<React.SVGProps<SVGSVGElement>>;
  const CookieIcon = MdCookie as React.FC<React.SVGProps<SVGSVGElement>>;

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div className="text-center mb-8 py-16">
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
          <PrivacyIcon className="w-8 h-8 text-white" />
        </div>
        <h1
          className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-4`}
        >
          Privacy Policy
        </h1>
        <p className={`${CommonClasses.textGray}`}>
          Last updated: {new Date().toLocaleDateString()}
        </p>
      </div>

      <div className={`${GlobalStyles.sectionWrap}`}>
        <div className="space-y-8">
          {/* Introduction */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              1. Introduction
            </h2>
            <p className={`${CommonClasses.textGray} mb-3`}>
              OFS Security Ledger ("we," "our," or "us") is committed to
              protecting your privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you
              use our platform.
            </p>
            <p className={`${CommonClasses.textGray}`}>
              By using OFS Security Ledger, you consent to the data practices
              described in this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              2. Information We Collect
            </h2>

            <h3
              className={`font-semibold ${CommonClasses.textGrayStrong} mb-3`}
            >
              Personal Information
            </h3>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside mb-4`}
            >
              <li>Full name and contact information</li>
              <li>Email address and phone number</li>
              <li>Government-issued identification for KYC verification</li>
              <li>Date of birth and residential address</li>
              <li>Financial information and transaction history</li>
            </ul>

            <h3
              className={`font-semibold ${CommonClasses.textGrayStrong} mb-3`}
            >
              Technical Information
            </h3>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside mb-4`}
            >
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Usage data and platform interaction metrics</li>
              <li>Cookie and tracking technology data</li>
            </ul>

            <h3
              className={`font-semibold ${CommonClasses.textGrayStrong} mb-3`}
            >
              Financial Information
            </h3>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside`}
            >
              <li>Wallet addresses and transaction hashes</li>
              <li>Cryptocurrency balances and trading history</li>
              <li>Bank account information (where applicable)</li>
              <li>Tax identification numbers</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              3. How We Use Your Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <DataIcon className="w-6 h-6 text-blue-600 mb-2" />
                <h4 className="font-semibold mb-2">Service Provision</h4>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  To create and manage your account, process transactions, and
                  provide customer support.
                </p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <SecurityIcon className="w-6 h-6 text-green-600 mb-2" />
                <h4 className="font-semibold mb-2">Security & Compliance</h4>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  To verify identity, prevent fraud, and comply with legal
                  obligations including AML/KYC regulations.
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                <CookieIcon className="w-6 h-6 text-purple-600 mb-2" />
                <h4 className="font-semibold mb-2">Platform Improvement</h4>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  To analyze usage patterns, improve our services, and develop
                  new features.
                </p>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                <PrivacyIcon className="w-6 h-6 text-orange-600 mb-2" />
                <h4 className="font-semibold mb-2">Communication</h4>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  To send important updates, security alerts, and marketing
                  communications (with consent).
                </p>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              4. Information Sharing and Disclosure
            </h2>
            <p className={`${CommonClasses.textGray} mb-3`}>
              We do not sell your personal information to third parties. We may
              share your information with:
            </p>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside mb-3`}
            >
              <li>
                <strong>Service Providers:</strong> Trusted partners who assist
                in platform operations, subject to strict data protection
                agreements
              </li>
              <li>
                <strong>Legal Authorities:</strong> When required by law,
                regulation, or legal process
              </li>
              <li>
                <strong>Financial Institutions:</strong> Banks and payment
                processors for transaction processing
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with mergers,
                acquisitions, or business transfers
              </li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              5. Data Security
            </h2>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
              <div className={`${CommonClasses.flexStart} gap-3`}>
                <SecurityIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p
                  className={`${CommonClasses.textSm} text-green-700 dark:text-green-300`}
                >
                  We implement industry-standard security measures including
                  encryption, access controls, and regular security audits to
                  protect your information.
                </p>
              </div>
            </div>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside`}
            >
              <li>256-bit SSL encryption for all data transmissions</li>
              <li>Multi-factor authentication for account access</li>
              <li>Regular security penetration testing</li>
              <li>Secure cold storage for cryptocurrency assets</li>
              <li>Employee training on data protection protocols</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              6. Data Retention
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              We retain your personal information for as long as necessary to
              fulfill the purposes outlined in this policy, unless a longer
              retention period is required or permitted by law. Typical
              retention periods include:
            </p>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside mt-3`}
            >
              <li>Account data: 7 years after account closure</li>
              <li>Transaction records: 7 years for regulatory compliance</li>
              <li>KYC documentation: 7 years after account verification</li>
              <li>Marketing data: Until consent is withdrawn</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              7. Your Rights
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Access & Correction</h4>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  Right to access and correct your personal information
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Data Portability</h4>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  Right to receive your data in a structured, machine-readable
                  format
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Erasure</h4>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  Right to request deletion of your personal information
                </p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Objection</h4>
                <p
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  Right to object to certain data processing activities
                </p>
              </div>
            </div>
          </section>

          {/* Cookies */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              8. Cookies and Tracking
            </h2>
            <p className={`${CommonClasses.textGray} mb-3`}>
              We use cookies and similar tracking technologies to enhance your
              experience on our platform:
            </p>
            <ul
              className={`${CommonClasses.textGray} space-y-2 list-disc list-inside`}
            >
              <li>
                <strong>Essential Cookies:</strong> Required for platform
                functionality and security
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Help us understand how users
                interact with our platform
              </li>
              <li>
                <strong>Preference Cookies:</strong> Remember your settings and
                preferences
              </li>
              <li>
                <strong>Marketing Cookies:</strong> Used for targeted
                advertising (with consent)
              </li>
            </ul>
          </section>

          {/* International Transfers */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              9. International Data Transfers
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              Your information may be transferred to and processed in countries
              other than your own. We ensure appropriate safeguards are in place
              for international data transfers, including standard contractual
              clauses and adequacy decisions.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              10. Children's Privacy
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              Our services are not intended for individuals under the age of 18.
              We do not knowingly collect personal information from children. If
              we become aware of such collection, we will take steps to delete
              the information.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              11. Changes to This Policy
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              We may update this Privacy Policy periodically. We will notify you
              of significant changes by posting the new policy on our platform
              and updating the "Last updated" date. Your continued use of our
              services after changes constitutes acceptance of the updated
              policy.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              12. Contact Information
            </h2>
            <p className={`${CommonClasses.textGray}`}>
              If you have any questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
              >
                <strong>Data Protection Officer:</strong>
                <br />
                Email: support@ofssecledger.com
                <br />
                <br />
                <strong>General Privacy Questions:</strong>
                <br />
                Email: support@ofssecledger.com
                {/* <br />
                <br /> */}
                {/* <strong>Address:</strong>
                <br />
                OFS Security Ledger
                <br />
                [Company Address]
                <br />
                [City, State, ZIP Code] */}
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
